// ScreenSelector.tsx
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { ScreenConfig } from "../../interfaces/journey";
import React from "react";
import "./ScreenSelector.css";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";

interface Props {
  value: ScreenConfig[];
  onChange: (screens: ScreenConfig[]) => void;
}

const label: Record<ScreenConfig["id"], string> = {
  cpf: "CPF",
  location: "Dispositivo",
  face: "Face ID",
  document: "Documento",
  documentoscopia: "Documentoscopia",
  facematch: "Facematch",
  bureau: "Bureau de Faces", // novo item
};

export const ScreenSelector: React.FC<Props> = React.memo(
  ({ value, onChange }) => {
    function handleDrag({ source, destination }: DropResult) {
      if (!destination) return;
      const reordered = [...value];
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      onChange(reordered);
    }

    const basicSteps = ["cpf", "location", "face", "document"];
    const validationSteps = ["documentoscopia", "facematch", "bureau"];

    function updateScreen(id: ScreenConfig["id"], patch: Partial<ScreenConfig>) {
      onChange(value.map(v => (v.id === id ? { ...v, ...patch } : v)));
    }

    function renderItem(s: ScreenConfig, idx: number) {
      return (
        <Draggable key={s.id} draggableId={s.id} index={idx}>
          {(dragProps: any, snapshot: any) => (
            <li
              ref={dragProps.innerRef}
              {...dragProps.draggableProps}
              className={`list-group-item d-flex align-items-center selector-item
                ${snapshot.isDragging ? "dragging" : ""}`}
            >
              {/* handle */}
              <span
                {...dragProps.dragHandleProps}
                aria-label="Arrastar para reordenar"
                className="handle text-muted me-3"
              >
                <FontAwesomeIcon icon={faGripVertical} />
              </span>

              {/* checkbox + label + select */}
              <div className="d-flex align-items-center justify-content-between flex-grow-1">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`step-${s.id}`}
                    checked={s.enabled}
                    disabled={
                      (s.id === "documentoscopia" &&
                        !value.find((v) => v.id === "document")?.enabled) ||
                      (s.id === "facematch" &&
                        !value.find((v) => v.id === "face")?.enabled)
                    }
                    onChange={() => {
                      if (s.id === "document") {
                        onChange(
                          value.map((v) =>
                            v.id === "document"
                              ? { ...v, enabled: !s.enabled }
                              : v.id === "documentoscopia"
                                ? { ...v, enabled: false } // força desmarcar filho
                                : v
                          )
                        );
                      } else if (s.id === "face") {
                        onChange(
                          value.map((v) =>
                            v.id === "face"
                              ? { ...v, enabled: !s.enabled }
                              : v.id === "facematch"
                                ? { ...v, enabled: false }
                                : v
                          )
                        );
                      } else {
                        onChange(
                          value.map((v) =>
                            v.id === s.id ? { ...v, enabled: !v.enabled } : v
                          )
                        );
                      }
                    }}
                  />
                  <label
                    className="form-check-label ms-2"
                    htmlFor={`step-${s.id}`}
                  >
                    {label[s.id]}
                  </label>
                </div>

                {/* dropdown condicional */}
                {!["cpf", "documentoscopia", "facematch"].includes(s.id) && (
                  <select className="form-select form-select-sm w-auto ms-3">
                    {s.id === "face" ? (
                      <option value="facetec">Facetec</option>
                    ) : s.id === "location" ? (
                      <option value="allowme">Allowme</option>
                    ) : s.id === "document" ? (
                      <option value="brscan">BRSCAN</option>
                    ) : s.id === "bureau" ? (
                      <>
                        <option value="serasa">Serasa</option>
                        <option value="serpro">Serpro</option>
                      </>
                    ) : (
                      <option value="allowme">Allowme</option>
                    )}
                  </select>
                )}
              </div>
            </li>
          )}
        </Draggable>
      );
    }

    return (
      <div className="card mb-4">
        <div className="card-header fw-semibold">Etapas da jornada</div>

        <DragDropContext onDragEnd={handleDrag}>
          {/* Grupo 1 - Etapas básicas */}
          <div className="mb-3">
            <Droppable droppableId="basic">
              {(droppableProps: any) => (
                <ul
                  className="list-group list-group-flush"
                  ref={droppableProps.innerRef}
                  {...droppableProps.droppableProps}
                >
                  {value
                    .filter((s) => basicSteps.includes(s.id))
                    .map((s, idx) => renderItem(s, idx))}
                  {droppableProps.placeholder}
                </ul>
              )}
            </Droppable>
          </div>

          {/* Grupo 2 - Validações */}
          <div>
            <div className="fw-bold px-3 pt-2">Validações</div>
            <ul className="list-group list-group-flush">
              {value
                .filter((s) => validationSteps.includes(s.id))
                .map((s) => {
                  const isFaceEnabled = value.find(
                    (v) => v.id === "face"
                  )?.enabled;
                  const isDocumentEnabled = value.find(
                    (v) => v.id === "document"
                  )?.enabled;

                  return (
                    <li
                      key={s.id}
                      className="list-group-item d-flex align-items-center selector-item"
                    >
                      <div className="d-flex align-items-center justify-content-between flex-grow-1">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`step-${s.id}`}
                            checked={s.enabled}
                            disabled={
                              (s.id === "facematch" &&
                                !value.find((v) => v.id === "face")?.enabled) ||
                              (s.id === "documentoscopia" &&
                                !value.find((v) => v.id === "document")
                                  ?.enabled)
                            }
                            onChange={() => {
                              if (s.id === "facematch" && !isFaceEnabled)
                                return;
                              if (
                                s.id === "documentoscopia" &&
                                !isDocumentEnabled
                              )
                                return;

                              onChange(
                                value.map((v) =>
                                  v.id === s.id
                                    ? { ...v, enabled: !v.enabled }
                                    : v
                                )
                              );
                            }}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor={`step-${s.id}`}
                          >
                            {label[s.id]}
                          </label>
                        </div>

                        {/* select só no bureau */}
                        {/* {s.id === "bureau" && (
                          <select className="form-select form-select-sm w-auto ms-3">
                            <option value="serasa">Serasa</option>
                            <option value="serpro">Serpro</option>
                          </select>
                        )} */}
                        {s.id === "bureau" && (
                          <select
                            className="form-select form-select-sm w-auto ms-3"
                            value={s.provider ?? "serasa"}
                            onChange={(e) =>
                              updateScreen("bureau", { provider: e.target.value as "serasa" | "serpro" })
                            }
                          >
                            <option value="serasa">Serasa</option>
                            <option value="serpro">Serpro</option>
                          </select>
                        )}
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </DragDropContext>
      </div>
    );
  }
);
